import { useState, useEffect } from 'react';
import { useLang } from '../contexts/LangContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Globe, DollarSign, Save } from 'lucide-react';

const CURRENCIES = [
  { code: 'IDR', name: 'Indonesian Rupiah (Rp)' },
  { code: 'USD', name: 'US Dollar ($)' },
  { code: 'EUR', name: 'Euro' },
  { code: 'CNY', name: 'Chinese Yuan' },
  { code: 'SAR', name: 'Saudi Riyal' },
  { code: 'MYR', name: 'Malaysian Ringgit' },
  { code: 'SGD', name: 'Singapore Dollar' },
];

const LANGUAGES = [
  { code: 'id', name: 'Bahasa Indonesia' },
  { code: 'en', name: 'English' },
];

export default function SettingsPage() {
  const { t, lang, setLanguage } = useLang();
  const [settings, setSettings] = useState({ language: lang, currency: 'IDR' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const res = await api.getSettings();
      setSettings({ language: res.data.language || lang, currency: res.data.currency || 'IDR' });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateSettings(settings);
      setLanguage(settings.language);
      toast.success(t('success'));
    } catch (err) { toast.error(t('error')); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-[#EB7B44] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-[#202020]" style={{ fontFamily: 'Manrope' }}>{t('settings')}</h1>
        <p className="text-sm text-[#64584F]">Configure application preferences</p>
      </div>

      <Card className="bg-white border-[#CACACC] rounded-xl shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#EB7B44]/10 flex items-center justify-center">
              <Globe size={20} className="text-[#EB7B44]" />
            </div>
            <CardTitle className="text-lg text-[#202020]" style={{ fontFamily: 'Manrope' }}>{t('language')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[#202020]">{t('language')}</Label>
            <Select value={settings.language} onValueChange={val => setSettings({...settings, language: val})}>
              <SelectTrigger className="border-[#CACACC] w-full max-w-xs" data-testid="settings-language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map(l => <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#CACACC] rounded-xl shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#3B82F6]/10 flex items-center justify-center">
              <DollarSign size={20} className="text-[#3B82F6]" />
            </div>
            <CardTitle className="text-lg text-[#202020]" style={{ fontFamily: 'Manrope' }}>{t('currency')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[#202020]">{t('currency')}</Label>
            <Select value={settings.currency} onValueChange={val => setSettings({...settings, currency: val})}>
              <SelectTrigger className="border-[#CACACC] w-full max-w-xs" data-testid="settings-currency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.code} - {c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#EB7B44] hover:bg-[#D96A35] text-white font-medium px-8"
          data-testid="settings-save-btn"
        >
          {saving ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <><Save size={16} className="mr-2" /> {t('save')}</>
          )}
        </Button>
      </div>
    </div>
  );
}
